import {
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import type { ComparativeAdvantageStrings } from "@/components/tools/comparative-advantage-calculator";
import { t } from "@/lib/i18n/interpolate";
import { minus } from "@/lib/tool-kit/econ-micro/format";
import type {
  Analysis,
  Producer,
  TradeGains,
  Winner,
} from "@/lib/tool-kit/econ-micro/trade";

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

export interface TradeViewProps {
  analysis: Analysis;
  producers: [Producer, Producer];
  names: [string, string];
  goods: [string, string];
  /** Hours per unit, when the data were entered that way. */
  hours: [[number, number], [number, number]] | null;
  price: number;
  gains: TradeGains | null;
  strings: ComparativeAdvantageStrings;
  fmt: Fmt;
}

function whoText(
  winner: Winner,
  names: [string, string],
  neither: string
): string {
  return winner === "neither" ? neither : names[winner];
}

function WorkingLines({
  producers,
  names,
  goods,
  hours,
  strings: s,
  fmt,
}: TradeViewProps) {
  const n = (v: number) => minus(fmt(v, { maximumFractionDigits: 4 }));
  return (
    <ul className="space-y-1 font-mono text-foreground text-sm">
      {producers.map((producer, i) => {
        const hoursFor = hours?.[i];
        const common = {
          producer: names[i],
          good1: goods[0],
          good2: goods[1],
          cost1: n(producer.good2 / producer.good1),
          cost2: n(producer.good1 / producer.good2),
        };
        return (
          <li key={names[i] + String(i)}>
            {hoursFor
              ? t(s.workHours, {
                  ...common,
                  t1: n(hoursFor[0]),
                  t2: n(hoursFor[1]),
                })
              : t(s.workOutput, {
                  ...common,
                  x: n(producer.good1),
                  y: n(producer.good2),
                })}
          </li>
        );
      })}
    </ul>
  );
}

export function TradeView(props: TradeViewProps) {
  const { analysis, names, goods, price, gains, strings: s, fmt } = props;
  const n = (v: number) => minus(fmt(v, { maximumFractionDigits: 4 }));
  const costHeader = (good: string, other: string) =>
    t(s.costColumn, { good, other });
  const rows = props.producers.map((producer, i) => [
    names[i],
    `${n(producer.good2 / producer.good1)} ${goods[1]}`,
    `${n(producer.good1 / producer.good2)} ${goods[0]}`,
  ]);
  const neither = s.neither;
  const { rangeLow: low, rangeHigh: high } = analysis;
  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{s.costHeading}</h3>
        <DataTable
          head={[
            s.producerColumn,
            costHeader(goods[0], goods[1]),
            costHeader(goods[1], goods[0]),
          ]}
          rows={rows}
        />
        <WorkingLines {...props} />
      </div>
      <StatGrid>
        <StatCard
          label={t(s.absoluteIn, { good: goods[0] })}
          value={whoText(analysis.absoluteGood1, names, neither)}
        />
        <StatCard
          label={t(s.absoluteIn, { good: goods[1] })}
          value={whoText(analysis.absoluteGood2, names, neither)}
        />
        <StatCard
          emphasis
          label={t(s.comparativeIn, { good: goods[0] })}
          value={whoText(analysis.comparativeGood1, names, neither)}
        />
        <StatCard
          emphasis
          label={t(s.comparativeIn, { good: goods[1] })}
          value={whoText(analysis.comparativeGood2, names, neither)}
        />
      </StatGrid>
      <p className="text-muted-foreground text-sm">{s.advantageNote}</p>
      {analysis.gainsFromTrade && low !== null && high !== null ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{s.tradeHeading}</h3>
          <p className="text-foreground text-sm">
            {t(s.rangeGood1, {
              good1: goods[0],
              good2: goods[1],
              low: n(low),
              high: n(high),
            })}
          </p>
          <p className="text-foreground text-sm">
            {t(s.rangeGood2, {
              good1: goods[0],
              good2: goods[1],
              low: n(1 / high),
              high: n(1 / low),
            })}
          </p>
          {gains ? (
            <p className="text-foreground text-sm">
              {gains.mutual
                ? t(s.gainsAt, {
                    price: n(price),
                    good1: goods[0],
                    good2: goods[1],
                    s1: names[analysis.comparativeGood1 === 0 ? 0 : 1],
                    s2: names[analysis.comparativeGood1 === 0 ? 1 : 0],
                    gain1: n(gains.specialist1),
                    gain2: n(gains.specialist2),
                  })
                : t(s.priceOutside, {
                    price: n(price),
                    low: n(low),
                    high: n(high),
                  })}
            </p>
          ) : null}
          <p className="text-muted-foreground text-sm">
            {t(s.totalGain, {
              gain: n(high - low),
              good1: goods[0],
              good2: goods[1],
            })}
          </p>
        </div>
      ) : (
        <p className="text-foreground text-sm">{s.noGains}</p>
      )}
    </div>
  );
}
